import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Image, Form } from 'react-bootstrap';
import '../../styles/login.css';
import { useThemeMode } from '../../ThemeContext';
import { authAPI, dataAPI } from '../../helpers/api_service';
import { MessageAlert, AlertDialog } from '../Common';
import sessionManager from '../../helpers/session_manager';
import ChevronRight from '../../assets/chevron-right.svg';
import Checkmark from '../../assets/checkmark.svg';
import EyeOpen from '../../assets/eye-open.svg';

// Import assets
import LoginBanner1 from '../../assets/loginban1.png';
import NoImage from '../../assets/noimage.svg';

function IndividualSignUp() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  
  // State for current step
  const [currentStep, setCurrentStep] = useState(1);
  
  // State for form data (removed company-related fields)
  const [formData, setFormData] = useState({
    profilePic: null,
    username: '',
    userEmail: '',
    fullName: '',
    industryTypeId: '',
    industryTypeName: '',
    regionId: '',
    regionName: '',
    userPassword: '',
    userPassword2: ''
  });
  
  // State for password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // State for OTP
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpTimer, setOtpTimer] = useState('00:30');
  const [canResendOtp, setCanResendOtp] = useState(false);
  
  // State for dropdown data
  const [regions, setRegions] = useState([]);
  const [industryTypes, setIndustryTypes] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  
  
  // State for form validation errors
  const [formErrors, setFormErrors] = useState({});
  
  // Refs for file inputs
  const profilePicRef = useRef(null);
  
  // Refs for OTP inputs
  const otpRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null)
  ];

  // Handle file upload for profile picture
  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        profilePic: file
      }));
    }
  };

  // Function to check if step 1 has any errors
  const hasStep1Errors = () => {
    let errors = {};
    if (!formData.username) {
      console.log('Username is required...');
      errors.username = true;
    }
    if (!formData.userEmail) {
      console.log('Email is required...');
      errors.userEmail = true;
    }
    if (!formData.fullName) {
      console.log('Full name is required...');
      errors.fullName = true;
    }
    if (!formData.industryTypeId) {
      console.log('Industry type is required...');
      errors.industryTypeId = true;
    }
    if (!formData.regionId) {
      console.log('Region is required...');
      errors.regionId = true;
    }
    if (!formData.userPassword) {
      console.log('Password is required...');
      errors.userPassword = true;
    }
    if (!formData.userPassword2) {
      console.log('Confirm password is required...');
      errors.userPassword2 = true;
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear form validation errors
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
    
    // Special validation for username
    if (name === 'username') {
      // Remove spaces and allow only letters, numbers, underscores, and dots
      const sanitizedValue = value.replace(/\s/g, '').replace(/[^a-zA-Z0-9._]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: sanitizedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle dropdown changes
  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'industryTypeId') {
      const selectedIndustry = industryTypes.find(industry => industry.id.toString() === value);
      setFormData(prev => ({
        ...prev,
        industryTypeId: value,
        industryTypeName: selectedIndustry ? selectedIndustry.name : ''
      }));
    } else if (name === 'regionId') {
      const selectedRegion = regions.find(region => region.id.toString() === value);
      setFormData(prev => ({
        ...prev,
        regionId: value,
        regionName: selectedRegion ? selectedRegion.name : ''
      }));
    }
  };

  // Handle OTP input changes
  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^[0-9]*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      
      // Auto-advance to next input
      if (value && index < 5) {
        otpRefs[index + 1].current?.focus();
      }
    }
  };

  // Handle OTP keydown (backspace)
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  // Toggle password visibility
  const togglePassword = (field) => {
    if (field === 'password') {
      setShowPassword(!showPassword);
    } else if (field === 'confirmPassword') {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  // Get eye icon based on visibility state
  const getEyeIcon = (isVisible) => {
    return isVisible ? '👁️' : '👁️‍🗨️'; // Using emoji as fallback since eye-off.svg doesn't exist
  };

  // Handle step navigation
  const goToStep = (step) => {
    setCurrentStep(step);
  };

  // State for API response
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Username validation function
  const validateUsername = (username) => {
    if (!username) return 'Username is required';
    if (username.length < 3) return 'Username must be at least 3 characters long';
    if (username.length > 30) return 'Username must be less than 30 characters';
    if (!/^[a-zA-Z0-9._]+$/.test(username)) return 'Username can only contain letters, numbers, underscores, and dots';
    if (username.startsWith('.') || username.endsWith('.')) return 'Username cannot start or end with a dot';
    if (username.includes('..')) return 'Username cannot contain consecutive dots';
    return null; // No error
  };

  // Handle form submissions (modified for 2 steps instead of 3)
  const handleStep1Submit = async (e) => {
    e.preventDefault();
    setApiError('');
    
    // Check for form validation errors first
    if (!hasStep1Errors()) {
      console.log('Form validation failed');
      return;
    }
    
    // Validate username format
    const usernameError = validateUsername(formData.username);
    if (usernameError) {
      setApiError(usernameError);
      return;
    }
    
    // Check if passwords match
    if (formData.userPassword !== formData.userPassword2) {
      setApiError('Passwords do not match');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create FormData for file uploads
      const formDataToSend = new FormData();
      
      // Required fields
      formDataToSend.append('company_type', 'individual');
      formDataToSend.append('username', formData.username);
      formDataToSend.append('email', formData.userEmail);
      formDataToSend.append('password', formData.userPassword);
      
      // Individual-specific fields (skip company fields)
      formDataToSend.append('region_id', formData.regionId);
      formDataToSend.append('industry_type_id', formData.industryTypeId);
      
      // User-specific fields
      formDataToSend.append('full_name', formData.fullName);
      formDataToSend.append('age', ''); // Not collected in form
      
      // File uploads
      if (formData.profilePic) {
        formDataToSend.append('profile_pic', formData.profilePic);
      }
      
      const data = await authAPI.register(formDataToSend);
      
      if (data.status === 'success') {
        goToStep(2);
      } else {
        setApiError(data.message || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setApiError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    setApiError('');
    
    const otpString = otp.join('');
    if (otpString.length === 6) {
      setIsSubmitting(true);
      
      try {
        const data = await authAPI.verifyOTP({
          email: formData.userEmail,
          otp_code: otpString
        });
        
        if (data.status === 'success') {
          // Start session using session manager (same as login)
          sessionManager.startSession(data.data.token);
          
          // Save additional user data for topbar (same as login)
          localStorage.setItem('userData', JSON.stringify(data.data));
          localStorage.setItem('userFullName', data.data.full_name || '');
          localStorage.setItem('userProfilePic', data.data.profile_pic || '');
          
          // Navigate to newSubscription page
          navigate('/newSubscription');
        } else {
          setApiError(data.message || 'OTP verification failed. Please try again.');
        }
      } catch (error) {
        console.error('OTP verification error:', error);
        setApiError('Network error. Please check your connection and try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Handle Resend OTP
  const handleResendOtp = async () => {
    setApiError('');
    
    try {
      const data = await authAPI.resendOTP({
        email: formData.userEmail
      });
      
      if (data.status === 'success') {
        // Reset timer to 30 seconds
        setOtpTimer('00:30');
        setCanResendOtp(false);
        // Clear OTP inputs
        setOtp(['', '', '', '', '', '']);
        // Focus first OTP input
        otpRefs[0].current?.focus();
      } else {
        setApiError(data.message || 'Failed to resend OTP. Please try again.');
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      setApiError('Network error. Please check your connection and try again.');
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (currentStep === 1) {
      navigate('/signup');
    } else {
      goToStep(currentStep - 1);
    }
  };

  // Fetch regions and industry types on component mount
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const data = await dataAPI.getRegionIndustryList();
        
        if (data.status === 'success') {
          setRegions(data.data.regions || []);
          setIndustryTypes(data.data.industry_types || []);
        } else {
          console.error('Failed to fetch dropdown data:', data.message);
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchDropdownData();
  }, []);

  // Reset OTP state when entering step 2
  useEffect(() => {
    if (currentStep === 2) {
      // Reset resend state and timer when entering OTP verification step
      setCanResendOtp(false);
      setOtpTimer('00:30');
      // Clear OTP inputs
      setOtp(['', '', '', '', '', '']);
    }
  }, [currentStep]);

  // OTP timer effect
  useEffect(() => {
    if (currentStep === 2 && !canResendOtp) {
      const timer = setInterval(() => {
        setOtpTimer(prev => {
          const [minutes, seconds] = prev.split(':').map(Number);
          if (minutes === 0 && seconds === 0) {
            setCanResendOtp(true);
            return '00:00';
          }
          if (seconds === 0) {
            return `${String(minutes - 1).padStart(2, '0')}:59`;
          }
          return `${String(minutes).padStart(2, '0')}:${String(seconds - 1).padStart(2, '0')}`;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [currentStep, canResendOtp]);

  return (
    <div className={`login-body ${mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
      <Container fluid className="login-container">
        <Row className="g-0 min-vh-100">
          {/* Left Side: Image/Overlay */}
          <Col lg={6} className="d-none d-lg-block login-left-bg position-relative">
            <div className="login-slider-img-wrap position-absolute w-100 h-100">
              <Image src={LoginBanner1} alt="Signup Banner" className="login-slider-img" fluid />
            </div>
            <div className="login-left-overlay"></div>
          </Col>
          
          {/* Right Side: Form */}
          <Col xs={12} lg={6} className="d-flex align-items-center justify-content-center login-right-bg position-relative">
            <div className="login-blur-bg-top"></div>
            <div className="login-blur-bg-bottom"></div>
            <div className="login-form-wrapper w-100 mt-4 mb-4" style={{ maxWidth: '486px' }}>
              
              {/* Stepper (modified for 2 steps) */}
              <div className="mb-4">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center gap-3 w-100">
                    <div className={`step-circle ${currentStep >= 1 ? 'step-active' : ''} ${currentStep > 1 ? 'step-complete' : ''}`}>
                      {currentStep > 1 ? <Image src={Checkmark} alt="Done" style={{ height: '18px' }} /> : '1'}
                    </div>
                    <div className={`step-line ${currentStep > 1 ? 'step-line-complete' : ''}`}></div>
                    <div className={`step-circle ${currentStep >= 2 ? 'step-active' : ''}`}>
                      2
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-between small text-white-50 mb-2">
                  <span className={`step-label ${currentStep >= 1 ? 'step-label-active' : ''}`}>Step : 1</span>
                  <span className={`step-label ${currentStep >= 2 ? 'step-label-active' : ''}`}>Step : 2</span>
                </div>
              </div>

              {/* Step 1: Personal Details Form (previously Step 2) */}
              {currentStep === 1 && (
                <div>
                  <div className="mb-4 text-center">
                    <h2 className="login-title" style={{ color: '#26c3c0' }}>Your Profile</h2>
                    <div className="login-subtitle" style={{ color: '#8a8a8a' }}>
                      Enter your details to create your individual account.
                    </div>
                  </div>
                  <Form noValidate onSubmit={handleStep1Submit}>
                    <Form.Group className="mb-3">
                      <div className="d-flex flex-column align-items-center">
                        <div className="position-relative" style={{ width: '100px', height: '100px' }}>
                          <input
                            type="file"
                            ref={profilePicRef}
                            accept="image/*"
                            onChange={handleProfilePicChange}
                            style={{ opacity: 0, width: '100px', height: '100px', position: 'absolute', zIndex: 2, cursor: 'pointer' }}
                          />
                          <div className="d-flex align-items-center justify-content-center upload-box bg-dark border border-secondary" 
                               style={{ width: '100px', height: '100px', borderRadius: '50%', overflow: 'hidden' }}>
                            <Image 
                              src={formData.profilePic ? URL.createObjectURL(formData.profilePic) : NoImage} 
                              alt="Profile Picture" 
                              style={{ width: '60px', height: '60px', objectFit: 'contain', opacity: formData.profilePic ? 1 : 0.5 }}
                            />
                          </div>
                        </div>
                        <label className="form-label mt-2 mb-0 text-center w-100" style={{ color: '#fff', fontWeight: 600 }}>
                          Upload profile picture
                        </label>
                      </div>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Control
                        type="text"
                        className="login-input"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        placeholder="Username"
                        required
                        title="Please Enter Username"
                        isInvalid={!formData.username && formErrors.username}
                        style={{ 
                          borderRadius: '10px',
                          backgroundColor: mode === 'dark' ? '#1a1a1a' : '#fff',
                          color: mode === 'dark' ? '#fff' : '#333',
                          border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`
                        }}
                      />
                      <Form.Control.Feedback type="invalid">
                        Please enter your username.
                      </Form.Control.Feedback>
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#888', 
                        marginTop: '4px',
                        fontStyle: 'italic'
                      }}>
                        Only letters, numbers, underscores, and dots allowed. No spaces.
                      </div>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Control
                        type="email"
                        className="login-input"
                        name="userEmail"
                        value={formData.userEmail}
                        onChange={handleInputChange}
                        placeholder="Email"
                        required
                        title="Please Enter Email Address"
                        isInvalid={!formData.userEmail && formErrors.userEmail}
                        style={{ 
                          borderRadius: '10px',
                          backgroundColor: mode === 'dark' ? '#1a1a1a' : '#fff',
                          color: mode === 'dark' ? '#fff' : '#333',
                          border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`
                        }}
                      />
                      <Form.Control.Feedback type="invalid">
                        Please enter your email address.
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Control
                        type="text"
                        className="login-input"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        placeholder="Full Name"
                        required
                        title="Please Enter Full Name"
                        isInvalid={!formData.fullName && formErrors.fullName}
                        style={{ 
                          borderRadius: '10px',
                          backgroundColor: mode === 'dark' ? '#1a1a1a' : '#fff',
                          color: mode === 'dark' ? '#fff' : '#333',
                          border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`
                        }}
                      />
                      <Form.Control.Feedback type="invalid">
                        Please enter your full name.
                      </Form.Control.Feedback>
                    </Form.Group>
                    

                    
                    {/* Industry Type */}
                    <Form.Group className="mb-3 position-relative">
                      <Form.Select
                        className="login-input"
                        name="industryTypeId"
                        value={formData.industryTypeId}
                        onChange={handleDropdownChange}
                        required
                        title="Please Select Industry Type from the list"
                        disabled={isLoadingData}
                        isInvalid={!formData.industryTypeId && formErrors.industryTypeId}
                        style={{ 
                          borderRadius: '10px', 
                          appearance: 'none',
                          backgroundColor: mode === 'dark' ? '#1a1a1a' : '#fff',
                          color: mode === 'dark' ? '#fff' : '#333',
                          border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`
                        }}
                      >
                        <option value="" disabled style={{ 
                          backgroundColor: mode === 'dark' ? '#1a1a1a' : '#fff',
                          color: mode === 'dark' ? '#fff' : '#333'
                        }}>
                          {isLoadingData ? 'Loading industry types...' : 'Select industry type'}
                        </option>
                        {industryTypes.map(industry => (
                          <option key={industry.id} value={industry.id} style={{ 
                            backgroundColor: mode === 'dark' ? '#1a1a1a' : '#fff',
                            color: mode === 'dark' ? '#fff' : '#333'
                          }}>
                            {industry.name}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        Please select industry type from the list.
                      </Form.Control.Feedback>
                      <Image 
                        src={ChevronRight} 
                        alt="Chevron" 
                        style={{ height: '16px', position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%) rotate(90deg)', pointerEvents: 'none', opacity: 0.7 }}
                      />
                    </Form.Group>
                    
                    {/* Region */}
                    <Form.Group className="mb-3 position-relative">
                      <Form.Select
                        className="login-input"
                        name="regionId"
                        value={formData.regionId}
                        onChange={handleDropdownChange}
                        required
                        title="Please Select Region from the list"
                        disabled={isLoadingData}
                        isInvalid={!formData.regionId && formErrors.regionId}
                        style={{ 
                          borderRadius: '10px', 
                          appearance: 'none',
                          backgroundColor: mode === 'dark' ? '#1a1a1a' : '#fff',
                          color: mode === 'dark' ? '#fff' : '#333',
                          border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`
                        }}
                      >
                        <option value="" disabled style={{ 
                          backgroundColor: mode === 'dark' ? '#1a1a1a' : '#fff',
                          color: mode === 'dark' ? '#fff' : '#333'
                        }}>
                          {isLoadingData ? 'Loading regions...' : 'Select Region'}
                        </option>
                        {regions.map(region => (
                          <option key={region.id} value={region.id} style={{ 
                            backgroundColor: mode === 'dark' ? '#1a1a1a' : '#fff',
                            color: mode === 'dark' ? '#fff' : '#333'
                          }}>
                            {region.name}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        Please select region from the list.
                      </Form.Control.Feedback>
                      <Image 
                        src={ChevronRight} 
                        alt="Chevron" 
                        style={{ height: '16px', position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%) rotate(90deg)', pointerEvents: 'none', opacity: 0.7 }}
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <div className="input-group" style={{ borderRadius: '10px' }}>
                        <Form.Control
                          type={showPassword ? 'text' : 'password'}
                          className="login-input"
                          name="userPassword"
                          value={formData.userPassword}
                          onChange={handleInputChange}
                          placeholder="Password"
                          required
                          title="Please Enter Password"
                          isInvalid={!formData.userPassword && formErrors.userPassword}
                          style={{ 
                            borderRadius: '10px 0 0 10px',
                            backgroundColor: mode === 'dark' ? '#1a1a1a' : '#fff',
                            color: mode === 'dark' ? '#fff' : '#333',
                            border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`
                          }}
                        />
                        <span 
                          className="input-group-text border-0 px-2" 
                          style={{ borderRadius: '0 10px 10px 0', cursor: 'pointer' }}
                          onClick={() => togglePassword('password')}
                        >
                          {getEyeIcon(showPassword)}
                        </span>
                      </div>
                      <Form.Control.Feedback type="invalid">
                        Please enter your password.
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <div className="input-group" style={{ borderRadius: '10px' }}>
                        <Form.Control
                          type={showConfirmPassword ? 'text' : 'password'}
                          className="login-input"
                          name="userPassword2"
                          value={formData.userPassword2}
                          onChange={handleInputChange}
                          placeholder="Confirm Password"
                          required
                          title="Please Enter Confirm Password"
                          isInvalid={!formData.userPassword2 && formErrors.userPassword2}
                          style={{ 
                            borderRadius: '10px 0 0 10px',
                            backgroundColor: mode === 'dark' ? '#1a1a1a' : '#fff',
                            color: mode === 'dark' ? '#fff' : '#333',
                            border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`
                          }}
                        />
                        <span 
                          className="input-group-text border-0 px-2" 
                          style={{ borderRadius: '0 10px 10px 0', cursor: 'pointer' }}
                          onClick={() => togglePassword('confirmPassword')}
                        >
                          {getEyeIcon(showConfirmPassword)}
                        </span>
                      </div>
                      <Form.Control.Feedback type="invalid">
                        Please enter your confirm password.
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <Button 
                        type="button" 
                        variant="outline-secondary" 
                        className="d-flex align-items-center"
                        onClick={handleBack}
                      >
                        <Image 
                          src={ChevronRight} 
                          alt="Back" 
                          style={{ height: '16px', transform: 'rotate(180deg)', marginRight: '8px', filter: 'invert(40%) sepia(10%) saturate(300%) hue-rotate(140deg)' }}
                        />
                        Back
                      </Button>
                      <Button 
                        type="submit" 
                        className="btn btn-teal login-btn d-flex align-items-center"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Registering...' : 'Continue'}
                        <span className="ms-2">
                          <Image src={ChevronRight} alt="chevron" style={{ height: '14px' }} />
                        </span>
                      </Button>
                    </div>
                  </Form>
                  <MessageAlert 
                    type="error" 
                    show={!!apiError}
                    message={apiError}
                  />
                </div>
              )}

              {/* Step 2: OTP Verification (previously Step 3) */}
              {currentStep === 2 && (
                <div>
                  <div className="mb-4 text-center">
                    <h2 className="login-title" style={{ color: '#26c3c0' }}>Verify Your Email Address</h2>
                    <div className="login-subtitle" style={{ color: '#8a8a8a' }}>
                      We've sent a 6-digit verification code to your email. Please enter it below to complete your sign-up.{' '}
                      <span style={{ color: '#26c3c0', fontWeight: 600 }}>{formData.userEmail}</span>
                    </div>
                  </div>
                  <div className="mb-4 text-center">
                    <h2 className="login-title" style={{ color: '#fff', fontSize: '1.5rem' }}>Enter OTP</h2>
                  </div>
                  <Form onSubmit={handleStep2Submit}>
                    <div className="d-flex justify-content-center gap-2 mb-4">
                      {otp.map((digit, index) => (
                        <Form.Control
                          key={index}
                          ref={otpRefs[index]}
                          type="text"
                          maxLength={1}
                          className="otp-input"
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          autoComplete="one-time-code"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          style={{
                            width: '48px',
                            height: '48px',
                            textAlign: 'center',
                            fontSize: '1.5rem',
                            background: mode === 'dark' ? '#1a1a1a' : '#fff',
                            border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                            borderRadius: '10px',
                            color: mode === 'dark' ? '#fff' : '#333'
                          }}
                        />
                      ))}
                    </div>
                    
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <Button 
                        type="button" 
                        variant="outline-secondary" 
                        className="d-flex align-items-center"
                        onClick={handleBack}
                      >
                        <Image 
                          src={ChevronRight} 
                          alt="Back" 
                          style={{ height: '16px', transform: 'rotate(180deg)', marginRight: '8px', filter: 'invert(40%) sepia(10%) saturate(300%) hue-rotate(140deg)' }}
                        />
                        Back
                      </Button>
                      <Button 
                        type="submit" 
                        className="btn btn-teal login-btn d-flex align-items-center"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Verifying...' : 'Submit'}
                        <span className="ms-2">
                          <Image src={ChevronRight} alt="chevron" style={{ height: '14px' }} />
                        </span>
                      </Button>
                    </div>
                    
                    <div className="text-center mt-2" style={{ color: '#b0b0b0' }}>
                      {canResendOtp ? (
                        <span>
                          Didn't receive the code?{' '}
                          <Button
                            variant="link"
                            className="p-0"
                            style={{ color: '#26c3c0', textDecoration: 'underline', fontWeight: 600 }}
                            onClick={handleResendOtp}
                          >
                            Resend OTP
                          </Button>
                        </span>
                      ) : (
                        <span>Didn't receive the code? You can resend in <span style={{ fontWeight: 600 }}>{otpTimer}</span></span>
                      )}
                    </div>
                  </Form>
                  <MessageAlert 
                    type="error" 
                    show={!!apiError}
                    message={apiError}
                  />
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default IndividualSignUp; 